import { useState, useCallback } from 'react';
import { uploadProfilePhoto, deleteProfilePhoto, validateImageFile } from '@/services/profilePhotoService';
import { ProfilePhotoUploadState } from '@/features/profile/components/ProfilePhotoUpload.types';

export const useProfilePhoto = () => {
  const [state, setState] = useState<ProfilePhotoUploadState>({
    isUploading: false,
    isDeleting: false,
    error: null,
    progress: 0
  });

  const uploadPhoto = useCallback(async (file: File): Promise<{ success: boolean; url?: string; error?: string }> => {
    // Reset error state
    setState(prev => ({ ...prev, error: null }));

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      const error = validation.error || 'Invalid file';
      setState(prev => ({ ...prev, error }));
      return { success: false, error };
    }

    // Set uploading state
    setState(prev => ({ 
      ...prev, 
      isUploading: true, 
      progress: 0,
      error: null 
    }));

    try {
      const result = await uploadProfilePhoto({
        file,
        onProgress: (progress) => {
          setState(prev => ({ ...prev, progress }));
        }
      });

      if (result.success) {
        setState(prev => ({ 
          ...prev, 
          isUploading: false, 
          progress: 100,
          error: null 
        }));
      } else {
        setState(prev => ({ 
          ...prev, 
          isUploading: false, 
          progress: 0,
          error: result.error || 'Upload failed' 
        }));
      }

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        progress: 0,
        error: errorMessage 
      }));
      
      return { success: false, error: errorMessage };
    }
  }, []);

  const removePhoto = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    setState(prev => ({ 
      ...prev, 
      isDeleting: true, 
      error: null 
    }));

    try {
      const result = await deleteProfilePhoto();

      if (result.success) {
        setState(prev => ({ 
          ...prev, 
          isDeleting: false, 
          error: null 
        }));
      } else {
        setState(prev => ({ 
          ...prev, 
          isDeleting: false, 
          error: result.error || 'Delete failed' 
        }));
      }

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Delete failed';
      setState(prev => ({ 
        ...prev, 
        isDeleting: false, 
        error: errorMessage 
      }));
      
      return { success: false, error: errorMessage };
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({
      isUploading: false,
      isDeleting: false,
      error: null,
      progress: 0
    });
  }, []);

  return {
    state,
    uploadPhoto,
    removePhoto,
    clearError,
    reset
  };
};

